import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import { getAccessoriesData, getHomepage } from "utils";
import { Button, ProductAddingModal, SpecificationTextModal } from "components";
import { ProductAccessoriesModal } from "components/_Modals/ProductAccessoriesModal/ProductAccessoriesModal";
import { useConfiguratorPopupContext } from "providers";
import { unitOptions } from "variables";
import { Select } from "components/Select/Select";
import { useTranslate } from "hooks";
import { NodeKinds } from "interfaces/enums";
import s from "components/_ProductConfigurator/ProductConfiguratorComponent.module.scss";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { getOrderCodeState } from "components/_ProductConfigurator/_helpers";
import { productConfiguratorTabsEnum } from "variables/enums";
import { ProductConfiguratorHeaderButtons } from "./ProductConfigurationForm/ProductConfiguratorHeaderButtons";
import { ProductBreadcrumb } from "../Breadcrumb/ProductBreadcrumb";
import { OrderCode } from "./OrderCode";
import { ProductConfiguratorHeaderTitle } from "./ProductConfiguratorHeaderTitle";
import { useAuthStore } from "stores/authStore";
import { FeatureFlagKey } from "utils/featureFlags/resolveFlags/featureFlags.type";

export const ProductConfiguratorHeader = Scrivito.connect(
  ({
    isModalView,
    isWizardView,
    activePart,
    activatePartItem,
    pageState,
    setActivePart,
    isConfigurationModalOpened,
    handleChangePageState,
    handleConfigurationModal,
    productName,
  }) => {
    const translate = useTranslate();
    const activeTab = useProductConfiguratorStore.use.activeTab();
    const isProductChanged = useProductConfiguratorStore.use.isProductChanged();
    const featureFlags = useAuthStore.use.featureFlags();

    const [productImage, setProductImage] = useState();

    const requestParams = useProductConfiguratorStore.use.requestParams();
    const productData = useProductConfiguratorStore.use.productData();

    const { showConfiguratorPopup, setParams } = useConfiguratorPopupContext();

    const {
      productImageList: { productImages },
      product: { orderCode, accessoryGroups, selectedAccessories },
      specificationText,
    } = productData || { productImageList: {}, product: {} };

    const prodImage = productImages?.filter(
      (image) => image.typ === "ProductPhoto",
    );
    const productImagePimId = prodImage && prodImage[0]?.pimId;

    const { productId, projectId } = Scrivito.currentPageParams();

    const unitOptionsList = useMemo(
      () =>
        unitOptions.map(({ value, label }) => ({
          value,
          label: translate(`PROJECT_UNITS_${label.toUpperCase()}`),
        })),
      [translate],
    );

    useEffect(() => {
      async function loadProductImage() {
        if (productImagePimId) {
          try {
            const obj = await Scrivito.load(() =>
              Scrivito.Obj.where("pimId", "equals", productImagePimId).first(),
            );

            if ((!productImage && obj) || productImage?.id() !== obj?.id()) {
              setProductImage(obj);
            }
          } catch (error) {
            console.error("Error loading product image:", error);
          }
        } else {
          setProductImage();
        }
      }

      loadProductImage();
    }, [productImages, productImage, productImagePimId]);

    const accessoriesData = useMemo(
      () => getAccessoriesData(selectedAccessories, accessoryGroups),
      [selectedAccessories, accessoryGroups],
    );

    const handleAddToProject = useCallback(() => {
      NiceModal.show(ProductAddingModal, {
        productImage,
        requestParams: {
          productName: productName,
          presentation: orderCode?.presentation,
          pimId: productImagePimId || "",
          orderCode: {
            seriesId: requestParams?.seriesId,
            state: {
              optionStates: orderCode && getOrderCodeState(orderCode?.parts),
            },
            propertyStates: requestParams?.state?.propertyStates,
            currentStrategyId: requestParams?.state?.currentStrategyId,
          },
        },
        accessoriesData,
      });
    }, [
      productImage,
      productName,
      orderCode,
      productImagePimId,
      requestParams?.seriesId,
      requestParams?.state?.propertyStates,
      requestParams?.state?.currentStrategyId,
      accessoriesData,
    ]);

    const toggleSpecificationTextModal = useCallback(
      () =>
        NiceModal.show(SpecificationTextModal, {
          productImage,
          productName: orderCode?.key,
          specificationText: specificationText?.text,
        }),
      [orderCode?.key, productImage, specificationText],
    );

    const navigateToProjectConfigurator = useCallback(() => {
      Scrivito.navigateTo(getHomepage()?.get("projectConfiguratorPage"), {
        projectId,
      });
    }, [projectId]);

    const { getProductRequestData } = useProductConfiguratorStore.use.actions();

    const handleNavigateBackToProject = useCallback(async () => {
      if (isProductChanged) {
        const productRequestData = getProductRequestData();
        setParams(() => ({
          id: productId,
          kind: NodeKinds.PRODUCT,
          productRequestData,
        }));
        showConfiguratorPopup(navigateToProjectConfigurator);
      } else {
        navigateToProjectConfigurator();
      }
    }, [
      isProductChanged,
      navigateToProjectConfigurator,
      showConfiguratorPopup,
      setParams,
      productId,
      getProductRequestData,
    ]);

    const handleOpenConfModal = useCallback(() => {
      handleConfigurationModal();
      setActivePart(null);
    }, [handleConfigurationModal, setActivePart]);

    const openProductAccessoryModal = () => {
      NiceModal.show(ProductAccessoriesModal, {
        initSelectedAccessories: selectedAccessories ?? [],
        accessoryGroups,
      });
    };

    const selectedAccessoriesNumber = Array.isArray(selectedAccessories)
      ? selectedAccessories.length
      : 0;

    const accessoryGroupsNumber = Array.isArray(accessoryGroups)
      ? accessoryGroups.length
      : 0;

    const { changeUnits } = useProductConfiguratorStore.use.actions();
    const { resultUnitsSystem } =
      useProductConfiguratorStore.use.requestParams() || {};

    const handleChangeUnits = useCallback(
      (newUnits) => {
        changeUnits(newUnits.value);
      },
      [changeUnits],
    );

    const isResultingListEnabled =
      featureFlags?.[FeatureFlagKey.PRODUCT_RESULTING_LIST];

    return (
      <div
        className={cx("py-4 bg-gray-100", {
          "py-0 bg-white": isModalView,
        })}
        id="product-configurator-header"
      >
        {!isModalView && (
          <ProductBreadcrumb productName={productName} projectId={projectId} />
        )}

        <div className="d-flex justify-content-between gap-4 flex-wrap align-items-center overflow-hidden">
          <div className="d-flex align-items-center gap-4 overflow-hidden w-100">
            <div className="d-flex flex-xl-row justify-content-between gap-4 w-100">
              <div className="d-flex justify-content-xl-between align-items-center gap-4 overflow-hidden min-width-0 flex-shrink-1">
                {productImage && (
                  <Scrivito.ImageTag
                    className="object-fit-contain mb-0"
                    width="110"
                    content={productImage}
                    height="110"
                  />
                )}
                <div
                  className="overflow-hidden flex-grow-1"
                  style={{
                    minWidth: 0,
                  }}
                >
                  <ProductConfiguratorHeaderTitle
                    productName={productName}
                    subtitle={translate("ORDER_CODE")}
                  />

                  <OrderCode
                    isModalView={isModalView}
                    isWizardView={isWizardView}
                    handleOpenConfModal={handleOpenConfModal}
                    activePart={activePart}
                    activatePartItem={activatePartItem}
                    isConfigurationModalOpened={isConfigurationModalOpened}
                  />
                </div>
              </div>

              {accessoryGroups && (
                <div className="d-flex flex-column align-items-start justify-content-center">
                  <ProductConfiguratorHeaderTitle
                    productName={translate("PRODUCT_CONFIGURATOR_ACCESSORIES")}
                    subtitle={`${selectedAccessoriesNumber}/${accessoryGroupsNumber} ${translate("PRODUCT_CONFIGURATOR_ACCESSORIES_ATTACHED")}`}
                  />
                  <div
                    className={cx(
                      s.headerButtons,
                      "d-flex flex-nowrap align-items-center",
                    )}
                  >
                    <div className="d-flex gap-1 align-items-center">
                      <Button
                        data-testid="product-configurator-view-accessories-btn"
                        onClick={openProductAccessoryModal}
                        color="outline-dark"
                        disabled={
                          isResultingListEnabled &&
                          activeTab ===
                            productConfiguratorTabsEnum.productSuggestion
                        }
                      >
                        {translate("PRODUCT_CONFIGURATOR_SHOW_ACCESSORIES")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex flex-column justify-content-between ms-auto">
                {!isModalView && (
                  <ProductConfiguratorHeaderButtons
                    toggleSpecificationTextModal={toggleSpecificationTextModal}
                    handleAddToProject={handleAddToProject}
                    handleNavigateBackToProject={handleNavigateBackToProject}
                  />
                )}

                {!isModalView && (
                  <div className="mt-1 ms-xl-auto">
                    <Select
                      options={unitOptionsList}
                      data-recording-disable={true}
                      value={unitOptionsList.find(
                        (item) => item.value === resultUnitsSystem,
                      )}
                      name="units"
                      label={translate("PROJECT_UNITS_WITH_ASTERISK")}
                      tooltipText={translate(
                        "PROJECT_UNITS_SELECT_TOOLTIP_TEXT",
                      )}
                      onChange={handleChangeUnits}
                      disabled={true}
                    />
                  </div>
                )}
              </div>

              {isModalView && !isConfigurationModalOpened && (
                <div className="d-inline-flex flex-shrink-0 align-self-end p-1 rounded-3 ms-auto mt-0">
                  <>
                    <Button
                      id="specification_text"
                      onClick={toggleSpecificationTextModal}
                      className={cx(
                        "rounded-1 cursor-pointer py-1 fs-18 lh-22",
                      )}
                      color="outline-dark"
                    >
                      {translate("PRODUCT_CONFIGURATOR_SPECIFICATION_TEXT")}
                    </Button>

                    <Button
                      id="technical_design"
                      onClick={handleChangePageState}
                      className={cx(
                        "rounded-1 cursor-pointer py-1 fs-18 lh-22 ms-3",
                      )}
                      color="dark"
                    >
                      {pageState === "result" ||
                      pageState === "technical_design"
                        ? translate(
                            "PRODUCT_CONFIGURATOR_PRODUCT_CONFIGURATION",
                          )
                        : translate("PRODUCT_CONFIGURATOR_TECHNICAL_DESIGN")}
                    </Button>
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);
